import { DataRoomAccessStatus, DataRoomRecipientKind } from './data-room.enums';

/**
 * How long an invitation link stays usable.
 *
 * The link is not a credential: it names an invitation, and the person still has to pass the
 * ordinary signup — including the one-hour email verification — before the invitation is worth
 * anything. That is what makes a fortnight safe rather than generous.
 */
export const DATA_ROOM_INVITE_TTL_DAYS = 14;

export const DATA_ROOM_INVITE_TTL_MS = DATA_ROOM_INVITE_TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * How long an Admin must wait before the same recipient can be sent another access email.
 *
 * Five minutes was the first answer, reasoning that nobody waits on this mail the way a person
 * waits on a verification code. In use it read as the feature being broken: the Admin who hears
 * "it never arrived" is usually on the phone to the recipient right then, and a quarter of an hour
 * of "try again in 287 seconds" is indistinguishable from a bug. Thirty seconds, the same as
 * verification, still stops a stuck click from filling a stranger's inbox.
 */
export const DATA_ROOM_INVITE_RESEND_DELAY_MS = 30 * 1000;

/**
 * Milliseconds still to wait before another access email may go to this recipient, 0 when it may
 * go now. Shared so the button the client greys out and the request the server refuses are gated
 * by the same arithmetic rather than two guesses at it.
 */
export const dataRoomInviteResendWaitMs = (
  inviteSentAt: Date | null | string | undefined,
  now: Date = new Date(),
): number => {
  if (!inviteSentAt) return 0;

  const elapsed = now.getTime() - new Date(inviteSentAt).getTime();

  return Math.max(0, DATA_ROOM_INVITE_RESEND_DELAY_MS - elapsed);
};

/**
 * Why an invitation link cannot be used. Each branch is its own code so the page can say what
 * happened instead of showing an unexplained 404 — a person who followed a link from their inbox
 * has done nothing wrong and deserves to be told which of these it is.
 */
export enum DataRoomInviteRefusal {
  /** The signed-in account is not the address the invitation was issued to. */
  EMAIL_MISMATCH = 'invite-email-mismatch',
  /** Still pending, but past its fourteen days. */
  EXPIRED = 'invite-expired',
  /** Access was withdrawn after the invitation went out. */
  REVOKED = 'invite-revoked',
  /** No access row carries this token — never issued, or replaced by a newer one. */
  UNKNOWN = 'invite-unknown',
}

export type DataRoomInviteState = {
  accessStatus: DataRoomAccessStatus;
  expiresAt: Date | null | string;
};

/**
 * The single decision on whether a link still opens the door, and the only place that knows it.
 *
 * A token is deliberately not extinguished by erasing its hash, because an erased hash makes
 * "accepted", "withdrawn" and "never existed" indistinguishable — the person would get the same
 * blank refusal in all three cases. What retires a token is the state of the access row it points
 * at: revoking moves the row to Revoked, and a resend overwrites the hash so the earlier link
 * resolves to nothing at all. In both cases the link stops working the moment the action lands,
 * which is the requirement; the row is what remembers why.
 *
 * Being used is not one of those things. The newest link belongs to whoever still has access, and
 * the email it arrived in is the way back to the room — a recipient who clicks it a second time is
 * not making a mistake to be corrected with a dead end. Nor does the link grant anything on its
 * own: it names an invitation, and the holder still has to be signed in as the invited address, so
 * keeping it live costs none of what the fourteen days protect.
 *
 * That is also why the expiry is only asked about while the row is Pending. The clock exists to
 * stop an invitation nobody answered from waiting forever; once it has been answered there is
 * nothing left for it to expire.
 */
export const dataRoomInviteRefusal = (
  { accessStatus, expiresAt }: DataRoomInviteState,
  now: Date = new Date(),
): DataRoomInviteRefusal | null => {
  if (accessStatus === DataRoomAccessStatus.REVOKED) return DataRoomInviteRefusal.REVOKED;
  if (accessStatus === DataRoomAccessStatus.ACTIVE) return null;

  if (!expiresAt || new Date(expiresAt).getTime() <= now.getTime()) {
    return DataRoomInviteRefusal.EXPIRED;
  }

  return null;
};

/** Emails are the identity here, so they are compared the way the unique index stores them. */
export const isSameInviteEmail = (left: string, right: string): boolean =>
  left.trim().toLowerCase() === right.trim().toLowerCase();

/**
 * Whether this recipient needs an invitation link of their own.
 *
 * Pending is not enough. An internal stakeholder who has not joined the company yet is Pending
 * too, and 20.2.4.3 §2 is explicit that they wait on the ordinary company invitation — "the same
 * existing mechanism used company-wide", "with no separate Data-Room-specific acceptance step on
 * top of that". Only an external recipient, who has no other way in, gets a link.
 */
export const needsInviteToken = ({
  recipientKind,
  status,
}: {
  recipientKind: DataRoomRecipientKind;
  status: DataRoomAccessStatus;
}): boolean =>
  status === DataRoomAccessStatus.PENDING && recipientKind === DataRoomRecipientKind.EXTERNAL;
